/**
 * Mietvertrag-Vorlage mit Platzhaltern (Plan Frage 11/12, Festlegung 9).
 * Die Vorlage ist je Laden bearbeitbar (Einstellungen › Regeln); `{{NAME}}` wird beim Erzeugen ersetzt,
 * eckige Klammern wie `[FRIST]` bleiben als „noch auszufüllen“ sichtbar.
 * Der Standardtext stammt aus dem Screen druck/Mietvertrag und ist juristisch nicht geprüft.
 */

export const PLATZHALTER = [
  "VERTRAGSNUMMER",
  "LADEN",
  "LADEN_INHABER",
  "LADEN_ADRESSE",
  "STEUERNUMMER",
  "MIETER",
  "MIETERNUMMER",
  "MIETER_ANSCHRIFT",
  "MIETER_EMAIL",
  "REGAL",
  "REGAL_GROESSE",
  "REGAL_LAGE",
  "LAUFZEIT",
  "START",
  "ENDE",
  "MIETE",
  "EINHEIT",
  "PROVISION",
  "KAUTION",
  "STEUERHINWEIS",
] as const;
export type Platzhalter = (typeof PLATZHALTER)[number];

export const STANDARD_VERTRAGSVORLAGE = `# Mietvertrag Regal {{REGAL}}
Vertrag {{VERTRAGSNUMMER}} · Miete einer Regalfläche zum Verkauf eigener Waren im Laden

Vermieter: {{LADEN}}, Inhaber: {{LADEN_INHABER}}, {{LADEN_ADRESSE}}, Steuernummer {{STEUERNUMMER}}
Mieter: {{MIETER}}, Mieternummer {{MIETERNUMMER}}, {{MIETER_ANSCHRIFT}}, {{MIETER_EMAIL}}

## § 1 Mietgegenstand
Vermietet wird Regal {{REGAL}} im Verkaufsraum ({{REGAL_GROESSE}}, {{REGAL_LAGE}}). Der Mieter darf die Fläche zum Verkauf eigener Waren nutzen.

## § 2 Zeitraum
{{LAUFZEIT}} Einräumen ist ab Mietbeginn während der Öffnungszeiten möglich.

## § 3 Miete, Provision, Kaution
Miete: {{MIETE}} je {{EINHEIT}}, im Voraus fällig. Provision: {{PROVISION}} vom Verkaufspreis nach Rabatt. Kaution: {{KAUTION}}, einmalig bei Mietbeginn.
Miete und Provision werden mit dem Verkaufserlös verrechnet. Die Kaution wird nach Mietende und Abholung der Ware zurückgezahlt. {{STEUERHINWEIS}}

## § 4 Einräumen durch den Mieter
Der Mieter räumt das Regal selbst ein und hält es ordentlich. Jeder Artikel trägt ein Etikett aus dem Mieterportal. Den Preis bestimmt der Mieter. Nicht erlaubt sind [AUSGESCHLOSSENE WAREN]. Der Laden darf Artikel ohne gültiges Etikett aus dem Regal nehmen und in die Rückgabe-Kiste legen.

## § 5 Haftung, Schwund, Versicherung
Der Laden behandelt die Ware mit üblicher Sorgfalt. Für Diebstahl oder Beschädigung durch Dritte haftet er nur bei Vorsatz oder grober Fahrlässigkeit. Festgestellter Schwund wird dem Mieter im Portal gemeldet. Die Ware ist nicht über den Laden versichert; eine eigene Versicherung ist Sache des Mieters.

## § 6 Abholung unverkaufter Ware
Unverkaufte Ware holt der Mieter innerhalb von 7 Tagen nach Mietende ab. Nicht abgeholte Ware wird [FRIST] in der Rückgabe-Kiste aufbewahrt. Danach darf der Laden sie spenden, wenn der Mieter dem zugestimmt hat.

## § 7 Abrechnung und Auszahlung
Verkäufe werden regelmäßig abgerechnet. Die Abrechnung steht im Mieterportal. Der Betrag nach Abzug von Provision und Miete wird auf das hinterlegte Konto überwiesen oder auf Wunsch bar ausgezahlt.`;

/** Offene Klammer-Platzhalter `[…]`, die der Laden noch ausfüllen muss. */
export function offenePlatzhalter(text: string): string[] {
  return [...new Set((text.match(/\[[A-ZÄÖÜ][A-ZÄÖÜ -]{1,40}\]/g) ?? []).map((m) => m))];
}

/** Unbekannte `{{…}}`-Platzhalter in der Vorlage. */
export function unbekanntePlatzhalter(text: string): string[] {
  const bekannt = new Set<string>(PLATZHALTER);
  return [...new Set((text.match(/\{\{([A-Z_]+)\}\}/g) ?? []).map((m) => m.slice(2, -2)))].filter((n) => !bekannt.has(n));
}

export function vertragRendern(vorlage: string, werte: Record<Platzhalter, string>): string {
  return vorlage.replace(/\{\{([A-Z_]+)\}\}/g, (ganz, name: string) => (name in werte ? werte[name as Platzhalter] : ganz)).replace(/\r\n/g, "\n").trim();
}

/** Laufzeit-Satz für § 2 je nach Buchungsart. */
export function laufzeitText(p: { art: "fest" | "abo"; start: string; ende: string | null; einheit: "woche" | "monat"; dauer?: number; kuendigungsfristWochen?: number }, formatTag: (t: string) => string): string {
  if (p.art === "abo") {
    const frist = p.kuendigungsfristWochen ?? 2;
    return `Das Mietverhältnis beginnt am ${formatTag(p.start)} und läuft als Miet-Abo je ${p.einheit === "woche" ? "Woche" : "Monat"} weiter, bis es von einer Seite mit einer Frist von ${frist} Wochen zum Monatsende gekündigt wird. Das Recht zur Kündigung aus wichtigem Grund bleibt unberührt.`;
  }
  const dauer = p.dauer ? ` (${p.dauer} ${p.einheit === "woche" ? (p.dauer === 1 ? "Woche" : "Wochen") : p.dauer === 1 ? "Monat" : "Monate"})` : "";
  return `Das Mietverhältnis läuft vom ${formatTag(p.start)} bis zum ${formatTag(p.ende ?? p.start)}${dauer} und endet, ohne dass es einer Kündigung bedarf. Eine Verlängerung ist in Absprache mit dem Laden möglich.`;
}

/** Vertragsversion: Jahr-Monat der Erstellung plus Kurzhash der Vorlage, z. B. `2026-10/7f3a`. */
export function vertragVersion(erstelltIso: string, vorlagenHashHex: string): string {
  return `${erstelltIso.slice(0, 7)}/${vorlagenHashHex.slice(0, 4)}`;
}

/** Prüfsumme lesbar in Vierergruppen: `7f3a 92c4 d1e0 c91e`. */
export function pruefsummeKurz(hashHex: string): string {
  return hashHex.slice(0, 16).replace(/(.{4})(?=.)/g, "$1 ");
}
